import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { MagnifyingGlassIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const LogsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');

  // Simulation de données de logs - remplacez par votre API réelle
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['logs'],
    queryFn: async () => {
      // Simulation d'appel API - remplacez par votre service de logs
      await new Promise(resolve => setTimeout(resolve, 500));
      return [
        {
          id: 1,
          level: 'info',
          message: 'Utilisateur connecté avec succès',
          timestamp: new Date(Date.now() - 60000).toISOString(),
          user: 'admin@example.com',
          ip: '192.168.1.1'
        },
        {
          id: 2,
          level: 'warning',
          message: 'Tentative de connexion avec mot de passe incorrect',
          timestamp: new Date(Date.now() - 120000).toISOString(),
          user: 'user@example.com',
          ip: '192.168.1.5'
        },
        {
          id: 3,
          level: 'error',
          message: 'Erreur de connexion à la base de données',
          timestamp: new Date(Date.now() - 180000).toISOString(),
          user: 'system',
          ip: 'localhost'
        },
        {
          id: 4,
          level: 'info',
          message: 'Nouveau produit ajouté',
          timestamp: new Date(Date.now() - 240000).toISOString(),
          user: 'admin@example.com',
          ip: '192.168.1.1'
        },
      ];
    },
  });

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = filterLevel === 'all' || log.level === filterLevel;
    return matchesSearch && matchesLevel;
  });

  const getLogIcon = (level) => {
    switch (level) {
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getLogLevelClass = (level) => {
    switch (level) {
      case 'error':
        return 'bg-red-50 border-l-red-500 text-red-700';
      case 'warning':
        return 'bg-yellow-50 border-l-yellow-500 text-yellow-700';
      case 'info':
      default:
        return 'bg-blue-50 border-l-blue-500 text-blue-700';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR');
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Journaux système</h2>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher dans les logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          <div>
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">Tous les niveaux</option>
              <option value="info">Info</option>
              <option value="warning">Avertissement</option>
              <option value="error">Erreur</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste des logs */}
      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-gray-500 mb-4">
            {searchTerm || filterLevel !== 'all' 
              ? 'Aucun log ne correspond aux critères de recherche'
              : 'Aucun log disponible'
            }
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="space-y-1 p-4">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className={`border-l-4 p-4 rounded-r-lg ${getLogLevelClass(log.level)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getLogIcon(log.level)}
                    <div className="flex-1">
                      <div className="font-medium text-sm mb-1">
                        {log.message}
                      </div>
                      <div className="text-xs opacity-75 space-y-1">
                        <div>Utilisateur: {log.user}</div>
                        <div>IP: {log.ip}</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs opacity-75 whitespace-nowrap ml-4">
                    {formatTimestamp(log.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination (si nécessaire) */}
      {filteredLogs.length > 50 && (
        <div className="flex justify-center">
          <div className="text-sm text-gray-500">
            Affichage de {Math.min(50, filteredLogs.length)} logs sur {filteredLogs.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsList;